import { ActivatedRouteSnapshot, BaseRouteReuseStrategy, Routes } from "@angular/router";
import { AppGuard } from "@app/app-resolver.service";
import { BaseComponent } from "@app/base.component";
import { ScopeGuard } from "@app/scope-guard.guard";
import { ConfirmProjectInvitationComponent } from "@components/confirm-project-invitation/confirm-project-invitation.component";
import { ProcessInvitationModalComponent } from "@components/process-invitation-modal/process-invitation-modal.component";
import { ExternalUserComponent } from "@pages/external-user/external-user.component";
import { HomeComponent } from "@pages/home/home.component";
import { NewProjectComponent } from "@pages/new-project/new-project.component";
import { AlarmDeviceConfigurationComponent } from "@pages/project/alarm-device-configuration/alarm-device-configuration.component";
import { FlameDetectorConfigurationComponent } from "@pages/project/flame-detector-configuration/flame-detector-configuration.component";
import { FloorplanResolver } from "@pages/project/floorplanner/floorplan-resolver";
import { FloorplannerComponent } from "@pages/project/floorplanner/floorplanner.component";
import { GasWarningCenterConfigurationComponent } from "@pages/project/gas-warning-center-configuration/gas-warning-center-configuration.component";
import { KvmConfigurationComponent } from "@pages/project/monitoring/kvm-configuration/kvm-configuration.component";
import { PlasticSignConfigurationComponent } from "@pages/project/plastic-sign-configuration/plastic-sign-configuration.component";
import { ProjectDetailsComponent } from "@pages/project/project-details/project-details.component";
import { SignalElementConfigurationComponent } from "@pages/project/signal-element-configuration/signal-element-configuration.component";
import { TransmitterConfigurationComponent } from "@pages/project/transmitter-configuration/transmitter-configuration.component";
import { ProjectReadAccessComponent } from "@pages/read-access/project-read-access.component";
import { ProjectReadAccessGuard } from "@pages/read-access/project-read-access.guard";
import { ScopeSelectionComponent } from "@pages/scope-selection/scope-selection.component";

import { ExternalUserGuard } from "@app/external-user-guard.guard";
import { onlineGuard } from "@pages/shared-project-link/online.guard";
import { SharedProjectLinkComponent } from "@pages/shared-project-link/shared-project-link.component";
import { GroupConfigurationComponent } from "@project/floorplanner/group-configuration/group-configuration.component";
import { CentralStationConfigurationComponent } from "@project/monitoring/central-station-configuration/central-station-configuration.component";
import { DisplayConfigurationComponent } from "@project/monitoring/display-configuration/display-configuration.component";
import { PatientMonitorConfigurationComponent } from "@project/monitoring/patient-monitor-configuration/patient-monitor-configuration.component";
import { TelemetryConfigurationComponent } from "@project/monitoring/telemetry-configuration/telemetry-configuration.component";
import { UtilityConfigurationComponent } from "@project/monitoring/utility-configuration/utility-configuration.component";
import { ProjectResolver } from "@project/project-resolver";
import { ProjectComponent } from "@project/project.component";

export const APP_ROUTES: Routes = [
  {
    path: "scope-selection",
    component: ScopeSelectionComponent,
    canActivate: [ScopeGuard],
  },
  {
    path: "external-user",
    component: ExternalUserComponent,
    canActivate: [ExternalUserGuard],
  },
  {
    path: "",
    component: BaseComponent,
    canActivate: [AppGuard],
    children: [
      {
        path: "",
        component: HomeComponent,
        canActivate: [ProjectReadAccessGuard],
      },
      {
        path: "projects/:id",
        component: ProjectComponent,
        resolve: {
          project: ProjectResolver,
        },
        canActivate: [ProjectReadAccessGuard],
        children: [
          {
            path: "",
            component: ProjectDetailsComponent,
          },
          {
            path: "floorplans/:floorplanId",
            component: FloorplannerComponent,
            data: {
              maximizeScreen: true,
            },
            resolve: {
              floorplan: FloorplanResolver,
            },
          },
          {
            path: "transmitter",
            component: TransmitterConfigurationComponent,
          },
          {
            path: "flame-detector",
            component: FlameDetectorConfigurationComponent,
          },
          {
            path: "alarm-device",
            component: AlarmDeviceConfigurationComponent,
          },
          {
            path: "gas-warning-center",
            component: GasWarningCenterConfigurationComponent,
          },
          {
            path: "signal-element",
            component: SignalElementConfigurationComponent,
          },
          {
            path: "plastic-sign",
            component: PlasticSignConfigurationComponent,
          },
          {
            path: "patient-monitor",
            component: PatientMonitorConfigurationComponent,
          },
          {
            path: "central-station",
            component: CentralStationConfigurationComponent,
          },
          {
            path: "telemetry",
            component: TelemetryConfigurationComponent,
          },
          {
            path: "kvm",
            component: KvmConfigurationComponent,
          },
          {
            path: "display",
            component: DisplayConfigurationComponent,
          },
          {
            path: "utility",
            component: UtilityConfigurationComponent,
          },
          {
            path: "group",
            component: GroupConfigurationComponent,
          },
        ],
      },
      {
        path: "new-project",
        component: NewProjectComponent,
        canActivate: [ProjectReadAccessGuard],
      },
      {
        path: "crm-receiver",
        component: NewProjectComponent,
        canActivate: [ProjectReadAccessGuard],
      },
      {
        path: "project-read-access",
        component: ProjectReadAccessComponent,
      },
    ],
  },
  {
    path: "projects/:id/share",
    component: SharedProjectLinkComponent,
    canActivate: [onlineGuard],
  },
  {
    path: "projects/:projectId/invitation/:invitationId/confirm",
    component: ConfirmProjectInvitationComponent,
    children: [
      {
        path: "modal",
        component: ProcessInvitationModalComponent,
      },
    ],
  },
  {
    path: "**",
    redirectTo: "",
  },
];

export class AppRouteReuseStrategy extends BaseRouteReuseStrategy {
  override shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
    if (future.component === FloorplannerComponent) {
      return false;
    }

    if (future.component === ProjectComponent && future.params["id"] !== curr.params["id"]) {
      return false;
    }
    return super.shouldReuseRoute(future, curr);
  }
}
